import { faEye } from '@fortawesome/pro-regular-svg-icons/faEye';
import { faEyeSlash } from '@fortawesome/pro-regular-svg-icons/faEyeSlash';
import { Form, Formik } from 'formik';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type Location,
  Link as RouterLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import ExerciseLogoIcon from 'src/assets/img/exercise-logo-icon.svg';
import { Alert } from 'src/components/Alert';
import { BackgroundGradient } from 'src/components/BackgroundGradient';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Card } from 'src/components/Card';
import { Checkbox } from 'src/components/Checkbox';
import { CircularProgress } from 'src/components/CircularProgress';
import { Container } from 'src/components/Container';
import { DrawerLayout } from 'src/components/Drawer';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { Meta } from 'src/components/Meta';
import { SiteFooter } from 'src/components/SiteFooter';
import { Stack } from 'src/components/Stack';
import { InputAdornment } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { styled } from 'src/components/muiTheme/utils';
import { Collapse } from 'src/components/transitions/Collapse';
import {
  isSessionAuthPending,
  useOptionalSession,
} from 'src/contexts/SessionContext';
import { externalLinks } from 'src/externalLinks';
import { TwoFactorRequired, User } from 'src/models/user/_types';
import {
  resendTwoFactorCode,
  signInUser,
  signOutUser,
  verifyTwoFactorCode,
} from 'src/models/user/user';
import { isAdmin } from 'src/models/user/utils';
import { isLoggedIn } from 'src/routes/routeAuth';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { getSafeRedirectUrl, safeRedirect } from 'src/utils/safeRedirect';
import * as Yup from 'yup';

import { TwoFactorPrompt } from './TwoFactorPrompt';

import type { Session } from 'src/models/session/_types';

const BackgroundWrapper = styled('div')(() => ({
  position: 'fixed',
  top: '0px',
  left: '0px',
  width: '100%',
  height: '100%',
  zIndex: 0,
}));

const PageWrapper = styled('div')(() => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
}));

type LoginLocationState = {
  unauthorizedMessage?: string;
  from?: string;
};

function resolveLoginRedirectTarget({
  location,
  session,
}: {
  location: Location;
  session: Session | undefined;
}): { redirectUrl: string; redirectPath: string } {
  const state = location.state as LoginLocationState | null;
  const searchParams = new URLSearchParams(location.search);
  const redirectTo = searchParams.get('redirect_to');
  const safeRedirectOptions = {
    sessionHost: session?.host,
    sessionAlternateHost: session?.alternateHost,
  };

  if (redirectTo) {
    return {
      redirectUrl: getSafeRedirectUrl(
        redirectTo,
        '/dashboard/',
        safeRedirectOptions
      ),
      redirectPath: '/',
    };
  }

  if (state?.from) {
    return {
      redirectUrl: '',
      redirectPath: getSafeRedirectUrl(state.from, '/', safeRedirectOptions),
    };
  }

  if (!session?.id) {
    return { redirectUrl: '/dashboard/', redirectPath: '/' };
  }

  return { redirectUrl: '', redirectPath: '/' };
}

const redirectToPlatformAdmin = async (platformId?: string) => {
  const adminUrl = externalLinks.platformAdmin(platformId);
  const isCrossOrigin = new URL(adminUrl).origin !== window.location.origin;

  if (isCrossOrigin) {
    await signOutUser();
  }

  window.location.href = adminUrl;
};

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    refreshSessionAndUser,
    user,
    userLoading,
    userFetching,
    session,
    sessionLoading,
    sessionFetching,
    isDarkmode,
    disableDarkmode,
    enableDarkmode,
  } = useOptionalSession();
  const { t } = useTranslation('translation', { keyPrefix: 'login' });

  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [redirectTargetReady, setRedirectTargetReady] = useState(false);
  const [hasCompletedAuthRedirect, setHasCompletedAuthRedirect] =
    useState(false);
  const [twoFactorData, setTwoFactorData] = useState<TwoFactorRequired | null>(
    null
  );
  const [twoFactorVerifying, setTwoFactorVerifying] = useState(false);
  const [twoFactorResending, setTwoFactorResending] = useState(false);
  const [twoFactorSuccessMessage, setTwoFactorSuccessMessage] = useState('');

  const completePostAuthRedirect = ({
    signedInUser,
    url,
    path,
  }: {
    signedInUser?: User;
    url: string;
    path: string;
  }) => {
    const treatAsAdminPortal =
      (signedInUser && isAdmin(signedInUser)) ||
      (isLoggedIn({ user: signedInUser ?? user, session, adminOnly: true }) &&
        !isLoggedIn({
          user: signedInUser ?? user,
          session,
          adminOnly: false,
        }));

    if (treatAsAdminPortal && (!url || !url.includes('/login'))) {
      void redirectToPlatformAdmin(
        session?.id != null ? String(session.id) : undefined
      );
      return;
    }

    if (url) {
      safeRedirect(url, '/dashboard/', {
        sessionHost: session?.host,
        sessionAlternateHost: session?.alternateHost,
      });
      return;
    }

    navigate(path, { replace: true });
  };

  const loginFormik = {
    initialValues: {
      email: new URLSearchParams(location.search).get('email') || '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required(t('email-required'))
        .email(t('invalid-email'))
        .transform((value) => value.replace(/\s+/g, '')),
      password: Yup.string().required(t('password-required')),
    }),
    onSubmit: async (values) => {
      try {
        setError('');
        const result = await signInUser(
          values.email,
          values.password,
          rememberMe
        );

        // Check if 2FA is required
        if ('twoFactorRequired' in result && result.twoFactorRequired) {
          setTwoFactorData(result);
          return;
        }

        // Normal login flow - result is a User object at this point
        const signedInUser = result as User;
        await refreshSessionAndUser();
        setHasCompletedAuthRedirect(true);
        completePostAuthRedirect({
          signedInUser,
          url: redirectUrl,
          path: redirectPath,
        });
      } catch (e) {
        const error = e as any;
        if (error?.response?.data?.message) {
          setError(error?.response?.data?.message);
        } else {
          setError(t('sign-in-error'));
        }
      }
    },
  };

  const handleTwoFactorVerify = async (otpCode: string) => {
    if (!twoFactorData) return;

    setTwoFactorVerifying(true);
    try {
      const signedInUser = await verifyTwoFactorCode(
        twoFactorData.userId,
        otpCode
      );
      await refreshSessionAndUser();

      // Clear 2FA state
      setTwoFactorData(null);
      setError('');

      setHasCompletedAuthRedirect(true);
      completePostAuthRedirect({
        signedInUser,
        url: redirectUrl,
        path: redirectPath,
      });
    } catch (e) {
      const error = e as any;
      setError(
        error?.response?.data?.message ||
          'Verification failed. Please try again.'
      );
    } finally {
      setTwoFactorVerifying(false);
    }
  };

  const handleTwoFactorResend = async () => {
    if (!twoFactorData) return;

    setTwoFactorResending(true);
    setTwoFactorSuccessMessage('');
    try {
      await resendTwoFactorCode(twoFactorData.userId);
      setTwoFactorSuccessMessage('Verification email has been resent!');
      setError('');
    } catch (e) {
      const error = e as any;
      setError(
        error?.response?.data?.message ||
          'Failed to resend code. Please try again.'
      );
    } finally {
      setTwoFactorResending(false);
    }
  };

  const authPending = isSessionAuthPending({
    user,
    userLoading,
    userFetching,
    session,
    sessionLoading,
    sessionFetching,
  });
  const isAdminPortalSession =
    !authPending &&
    isLoggedIn({ user, session, adminOnly: true }) &&
    !isLoggedIn({ user, session, adminOnly: false });
  const isAlreadyLoggedIn =
    !authPending &&
    (isLoggedIn({ user, session, adminOnly: false }) ||
      isLoggedIn({ user, session, adminOnly: true }));

  useEffect(() => {
    if (authPending || redirectTargetReady) {
      return;
    }

    const state = location.state as LoginLocationState | null;
    const { redirectUrl: resolvedUrl, redirectPath: resolvedPath } =
      resolveLoginRedirectTarget({ location, session });

    if (state?.unauthorizedMessage && !user?.id) {
      setError(state.unauthorizedMessage);
    }

    setRedirectUrl(resolvedUrl);
    setRedirectPath(resolvedPath);
    setRedirectTargetReady(true);

    // Clear router state only after the redirect target is captured in local state.
    if (state && (state.unauthorizedMessage || state.from)) {
      navigate(location.pathname + location.search, {
        replace: true,
        state: {},
      });
    }
  }, [
    authPending,
    location,
    navigate,
    redirectTargetReady,
    session,
    user?.id,
  ]);

  useEffect(() => {
    if (
      !isAlreadyLoggedIn ||
      !redirectTargetReady ||
      hasCompletedAuthRedirect
    ) {
      return;
    }

    setHasCompletedAuthRedirect(true);

    if (isAdminPortalSession) {
      void redirectToPlatformAdmin(
        session?.id != null ? String(session.id) : undefined
      );
      return;
    }

    if (redirectUrl) {
      safeRedirect(redirectUrl, '/dashboard/', {
        sessionHost: session?.host,
        sessionAlternateHost: session?.alternateHost,
      });
      return;
    }

    navigate(redirectPath, { replace: true });
  }, [
    hasCompletedAuthRedirect,
    isAdminPortalSession,
    isAlreadyLoggedIn,
    navigate,
    redirectPath,
    redirectTargetReady,
    redirectUrl,
    session?.alternateHost,
    session?.host,
    session?.id,
  ]);

  useLayoutEffect(() => {
    disableDarkmode();
    return () => {
      enableDarkmode();
    };
  }, [disableDarkmode, enableDarkmode]);

  const darkMode = isDarkmode();
  const logoUrl =
    darkMode && session?.darkmodeLogoUrl
      ? session.darkmodeLogoUrl
      : session?.logoUrl || ExerciseLogoIcon;

  if (authPending || isAlreadyLoggedIn) {
    return (
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress size="large" />
      </Box>
    );
  }

  return (
    <>
      <Meta title={t('sign-in')} brand={session?.brand} />
      <DrawerLayout drawerOpen={false}>
        <BackgroundWrapper>
          <BackgroundGradient color={error ? 'error' : 'primary'} />
        </BackgroundWrapper>

        <PageWrapper>
          <Box sx={{ flex: '1 0 auto', pb: 5 }}>
            <Container
              maxWidth="lg"
              sx={(theme) => ({
                [theme.breakpoints.down('md')]: {
                  mb: 4,
                  mt: 4,
                },
                mb: 6,
                mt: 8,
                position: 'relative',
              })}
            >
              <Stack
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                {!sessionLoading && session && (
                  <img
                    src={logoUrl}
                    style={{ maxHeight: '80px' }}
                    alt={`${session?.brand || 'Exercise'} Logo`}
                  />
                )}
              </Stack>
            </Container>
            <Container maxWidth="sm" sx={{ position: 'relative' }}>
              {twoFactorData ? (
                <TwoFactorPrompt
                  userId={twoFactorData.userId}
                  method={twoFactorData.method}
                  message={twoFactorData.message}
                  onVerify={handleTwoFactorVerify}
                  onResend={
                    twoFactorData.method === 'email'
                      ? handleTwoFactorResend
                      : undefined
                  }
                  isVerifying={twoFactorVerifying}
                  isResending={twoFactorResending}
                  error={error}
                  successMessage={twoFactorSuccessMessage}
                />
              ) : (
                <Card
                  sx={{ p: 2.5, bgcolor: 'transparent', boxShadow: 'none' }}
                >
                  <Stack
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h4" mb={1}>
                      {t('sign-in')}
                    </Typography>
                  </Stack>

                  <Collapse in={!!error} collapsedSize={0}>
                    <Alert severity="error" sx={{ mb: 3 }}>
                      {error}
                    </Alert>
                  </Collapse>

                  <Formik enableReinitialize={true} {...loginFormik}>
                    {({ isSubmitting, isValid, dirty }) => (
                      <Form>
                        <FormTextField
                          sx={{ mt: 0 }}
                          label={t('email')}
                          name="email"
                        />
                        <FormTextField
                          sx={{ mt: 3 }}
                          label={t('password')}
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment
                                position="end"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                <Icon
                                  size="small"
                                  icon={showPassword ? faEyeSlash : faEye}
                                />
                              </InputAdornment>
                            ),
                          }}
                        />

                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ mt: 3 }}
                        >
                          <Checkbox
                            label={t('remember-me')}
                            checked={rememberMe}
                            onChange={(event) =>
                              setRememberMe(event.target.checked)
                            }
                          />
                          <Link
                            variant="body2"
                            component={RouterLink}
                            to={`/reset-password/`}
                          >
                            {t('forgot-password')}
                          </Link>
                        </Stack>

                        <Button
                          variant="prominent"
                          sx={{ mt: 3 }}
                          disabled={isSubmitting || !isValid || !dirty}
                          loading={isSubmitting}
                          type="submit"
                          fullWidth
                        >
                          {t('sign-in')}
                        </Button>
                      </Form>
                    )}
                  </Formik>
                </Card>
              )}

              {(!session?.id || session?.freeSignups) && (
                <Typography
                  sx={{ mt: 5, position: 'relative' }}
                  align="center"
                  variant="body1"
                  color="text.secondary"
                >
                  {t('no-account')}
                  <br />
                  <Link component={RouterLink} to={`/sign-up`}>
                    {t('sign-up')}
                  </Link>
                </Typography>
              )}

              {session?.id && !session?.hideLandingPage && (
                <Typography
                  sx={{ mt: 5, position: 'relative' }}
                  align="center"
                  variant="body1"
                  color="text.secondary"
                >
                  {t('no-brand-account', {
                    article: /^[aeiou]/i.test(session.brand || '') ? 'an' : 'a',
                    brand: session.brand,
                  })}
                  <br />
                  <Link
                    component="a"
                    href={
                      session?.alternateLanding ||
                      externalLinks.landingPage(session.trainerId)
                    }
                  >
                    {t('click-here')}
                  </Link>{' '}
                  {t('to-see-options')}
                </Typography>
              )}
            </Container>
          </Box>
          <SiteFooter />
        </PageWrapper>
      </DrawerLayout>
    </>
  );
};
